import { AccordionStep, Question } from '@hb/domain';

import { EnergyLabel, PropertyType } from '@ap/ap-interface-apix-hypotheken-berekening';

const min = 0;
const max = 990000000;

export enum AnalyticType {
    AMOUNT = 'AMOUNT',
    DATE = 'DATE',
    AGE = 'AGE'
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace Keys {
    export function getValidationAmount(key: QuestionKey, error: string): number {
        return question.find((validator) => validator.key === key).validator[error];
    }

    export function getStep(key: QuestionKey | ButtonKey): number {
        return question.find((validator) => validator.key === key)?.step;
    }

    export function getStepAsStringByKey(key: QuestionKey | ButtonKey): string {
        const step = getStep(key);
        return getStepAsString(step);
    }

    export function getStepAsString(step: AccordionStep): string {
        switch (step) {
            case AccordionStep.INCOME:
                return 'INCOME';
            case AccordionStep.FINANCIAL_OBLIGATION:
                return 'FINANCIAL_OBLIGATION';
            case AccordionStep.PROPERTY:
                return 'PROPERTY';
            case AccordionStep.EXTRA_ASSET:
                return 'EXTRA_ASSET';
            case AccordionStep.MORTGAGE:
                return 'MORTGAGE';
        }
    }
}

export enum ButtonKey {
    APPOINTMENT_BUTTON = 'APPOINTMENT_BUTTON',
    INCOME_CALCULATOR = 'INCOME_CALCULATOR',
    CALCULATION_DETAILS_BUTTON = 'CALCULATION_DETAILS_BUTTON',
    MORTGAGE_INCOME = 'MORTGAGE_INCOME',
    MORTGAGE_INCOME_GROSS_AMOUNT = 'MORTGAGE_INCOME_GROSS_AMOUNT',
    MORTGAGE_INCOME_NET_AMOUNT = 'MORTGAGE_INCOME_NET_AMOUNT',
    EXPECTED_MORTGAGE = 'EXPECTED_MORTGAGE',
    EXPECTED_MORTGAGE_GROSS_AMOUNT = 'EXPECTED_MORTGAGE_GROSS_AMOUNT',
    EXPECTED_MORTGAGE_NET_AMOUNT = 'EXPECTED_MORTGAGE_NET_AMOUNT',
    CLOSING_COST_TITLE = 'CLOSING_COST_TITLE',
    CLOSING_COST_TRANSFER_TAX = 'CLOSING_COST_TRANSFER_TAX',
    CLOSING_COST_NHG = 'CLOSING_COST_NHG',
    CLOSING_COST_BANK_GUARANTEE = 'CLOSING_COST_BANK_GUARANTEE',
    PURCHASE_COST_PROPERTY = 'PURCHASE_COST_PROPERTY',
    REQUIRED_ASSET_DEFICIENCY = 'REQUIRED_ASSET_DEFICIENCY',
    REQUIRED_ASSET_OWN_INVESTMENT_RENOVATION = 'REQUIRED_ASSET_OWN_INVESTMENT_RENOVATION',
    REQUIRED_ASSET_OWN_INVESTMENT_SUSTAINABILITY = 'REQUIRED_ASSET_OWN_INVESTMENT_SUSTAINABILITY'
}

export enum QuestionKey {
    INCOME = 'INCOME',
    INCOME_CO_APPLICANT = 'INCOME_CO_APPLICANT',
    INCOME_MAIN_APPLICANT_DATE_OF_BIRTH = 'INCOME_MAIN_APPLICANT_DATE_OF_BIRTH',
    INCOME_CO_APPLICANT_DATE_OF_BIRTH = 'INCOME_CO_APPLICANT_DATE_OF_BIRTH',
    INCOME_AOW10YEARS_INFO = 'INCOME_AOW10YEARS_INFO',
    INCOME_STARTERS_DISPENSATION = 'INCOME_STARTERS_DISPENSATION',
    INCOME_STARTERS_DISPENSATION_INFO = 'INCOME_STARTERS_DISPENSATION_INFO',
    FINANCIAL_OBLIGATION_OPTIONS = 'FINANCIAL_OBLIGATION_OPTIONS',
    OPTION_STUDENT_DEBT = 'OPTION_STUDENT_DEBT',
    OPTION_PAYMENT_ACCOUNT_OVERDRAFT = 'OPTION_PAYMENT_ACCOUNT_OVERDRAFT',
    OPTION_MOBILE_PHONE = 'OPTION_MOBILE_PHONE',
    OPTION_OPEN_ENDED_CREDIT = 'OPTION_OPEN_ENDED_CREDIT',
    OPTION_CREDIT_CARD = 'OPTION_CREDIT_CARD',
    OPTION_PRIVATE_LEASE = 'OPTION_PRIVATE_LEASE',
    OPTION_PERSONAL_LOAN = 'OPTION_PERSONAL_LOAN',
    OPTION_PARTNER_ALIMONY = 'OPTION_PARTNER_ALIMONY',
    OPTION_NONE = 'OPTION_NONE',
    STUDENT_DEBT_TOTAL = 'STUDENT_DEBT_TOTAL',
    PAYMENT_ACCOUNT_OVERDRAFT_AMOUNT = 'PAYMENT_ACCOUNT_OVERDRAFT_AMOUNT',
    PAYMENT_ACCOUNT_OVERDRAFT_TIP = 'PAYMENT_ACCOUNT_OVERDRAFT_TIP',
    MOBILE_PHONE_AMOUNT = 'MOBILE_PHONE_AMOUNT',
    OPEN_ENDED_CREDIT_AMOUNT = 'OPEN_ENDED_CREDIT_AMOUNT',
    CREDIT_CARD_AMOUNT = 'CREDIT_CARD_AMOUNT',
    CREDIT_CARD_BUTTON_GROUP = 'CREDIT_CARD_BUTTON_GROUP',
    CREDIT_CARD_TIP = 'CREDIT_CARD_TIP',
    PRIVATE_LEASE_AMOUNT = 'PRIVATE_LEASE_AMOUNT',
    PRIVATE_LEASE_AMOUNT_CO_APPLICANT = 'PRIVATE_LEASE_AMOUNT_CO_APPLICANT',
    PRIVATE_LEASE_MONTHS = 'PRIVATE_LEASE_MONTHS',
    PRIVATE_LEASE_MONTHS_CO_APPLICANT = 'PRIVATE_LEASE_MONTHS_CO_APPLICANT',
    PERSONAL_LOAN_TOTAL_AMOUNT = 'PERSONAL_LOAN_TOTAL_AMOUNT',
    PERSONAL_LOAN_MONTHLY_PAYMENT = 'PERSONAL_LOAN_MONTHLY_PAYMENT',
    PARTNER_ALIMONY_AMOUNT = 'PARTNER_ALIMONY_AMOUNT',
    PROPERTY_TYPE_BUTTON_GROUP = 'PROPERTY_TYPE_BUTTON_GROUP',
    ENERGY_LABEL_RADIO = 'ENERGY_LABEL_RADIO',
    PURCHASE_PRICE_AMOUNT = 'PURCHASE_PRICE_AMOUNT',
    SUSTAINABILITY_LOAN_BUTTON_GROUP = 'SUSTAINABILITY_LOAN_BUTTON_GROUP',
    SUSTAINABILITY_AMOUNT = 'SUSTAINABILITY_AMOUNT',
    RENOVATION = 'RENOVATION',
    RENOVATION_AMOUNT = 'RENOVATION_AMOUNT',
    OWN_PROPERTY = 'OWN_PROPERTY',
    SELLING_PRICE = 'SELLING_PRICE',
    MORTGAGE_AMOUNT = 'MORTGAGE_AMOUNT',
    OWN_MONEY = 'OWN_MONEY',
    OWN_MONEY_AMOUNT = 'OWN_MONEY_AMOUNT'
}

export const question: Question[] = [
    {
        key: QuestionKey.INCOME,
        validator: {
            min: 0,
            max: 1000000
        },
        step: AccordionStep.INCOME,
        analytics: {
            type: AnalyticType.AMOUNT,
            range: [min, 5000, 10000, 15000, 20000, 30000, 40000, 50000, 75000, 100000, 150000, 200000, 250000, 300000, 400000, 500000, max]
        }
    },
    {
        key: QuestionKey.INCOME_CO_APPLICANT,
        validator: {
            min: 0,
            max: 1000000
        },
        step: AccordionStep.INCOME,
        analytics: {
            type: AnalyticType.AMOUNT,
            range: [min, 5000, 10000, 15000, 20000, 30000, 40000, 50000, 75000, 100000, 150000, 200000, 250000, 300000, 400000, 500000, max]
        }
    },
    {
        key: QuestionKey.INCOME_MAIN_APPLICANT_DATE_OF_BIRTH,
        step: AccordionStep.INCOME,
        analytics: {
            type: AnalyticType.AGE,
            range: [min, 18, 25, 30, 35, 45, 57, 67, 75, max]
        }
    },
    {
        key: QuestionKey.INCOME_CO_APPLICANT_DATE_OF_BIRTH,
        step: AccordionStep.INCOME,
        analytics: {
            type: AnalyticType.AGE,
            range: [min, 18, 25, 30, 35, 45, 57, 67, 75, max]
        }
    },
    {
        key: QuestionKey.INCOME_AOW10YEARS_INFO,
        step: AccordionStep.INCOME
    },
    {
        key: QuestionKey.INCOME_STARTERS_DISPENSATION,
        step: AccordionStep.INCOME
    },
    {
        key: QuestionKey.INCOME_STARTERS_DISPENSATION_INFO,
        step: AccordionStep.INCOME
    },
    {
        key: QuestionKey.FINANCIAL_OBLIGATION_OPTIONS,
        step: AccordionStep.FINANCIAL_OBLIGATION
    },
    {
        key: QuestionKey.OPTION_PARTNER_ALIMONY,
        step: AccordionStep.FINANCIAL_OBLIGATION
    },
    {
        key: QuestionKey.OPTION_PRIVATE_LEASE,
        step: AccordionStep.FINANCIAL_OBLIGATION
    },
    {
        key: QuestionKey.OPTION_PERSONAL_LOAN,
        step: AccordionStep.FINANCIAL_OBLIGATION
    },
    {
        key: QuestionKey.OPTION_STUDENT_DEBT,
        step: AccordionStep.FINANCIAL_OBLIGATION
    },
    {
        key: QuestionKey.OPTION_PAYMENT_ACCOUNT_OVERDRAFT,
        step: AccordionStep.FINANCIAL_OBLIGATION
    },
    {
        key: QuestionKey.OPTION_MOBILE_PHONE,
        step: AccordionStep.FINANCIAL_OBLIGATION
    },
    {
        key: QuestionKey.OPTION_CREDIT_CARD,
        step: AccordionStep.FINANCIAL_OBLIGATION
    },
    {
        key: QuestionKey.OPTION_OPEN_ENDED_CREDIT,
        step: AccordionStep.FINANCIAL_OBLIGATION
    },
    {
        key: QuestionKey.OPTION_NONE,
        step: AccordionStep.FINANCIAL_OBLIGATION
    },
    {
        key: QuestionKey.STUDENT_DEBT_TOTAL,
        validator: {
            min: 1,
            max: 2000
        },
        step: AccordionStep.FINANCIAL_OBLIGATION,
        analytics: {
            type: AnalyticType.AMOUNT,
            range: [min, 50, 100, 150, 200, 250, 500, max]
        }
    },
    {
        key: QuestionKey.PAYMENT_ACCOUNT_OVERDRAFT_AMOUNT,
        validator: {
            min: 1,
            max: 50000
        },
        step: AccordionStep.FINANCIAL_OBLIGATION,
        analytics: {
            type: AnalyticType.AMOUNT,
            range: [min, 500, 1000, 1500, 2000, 2500, 5000, max]
        }
    },
    {
        key: QuestionKey.MOBILE_PHONE_AMOUNT,
        validator: {
            min: 1,
            max: 4000
        },
        step: AccordionStep.FINANCIAL_OBLIGATION,
        analytics: {
            type: AnalyticType.AMOUNT,
            range: [min, 25, 50, 75, 100, 250, max]
        }
    },
    {
        key: QuestionKey.OPEN_ENDED_CREDIT_AMOUNT,
        validator: {
            min: 1,
            max: 100000
        },
        step: AccordionStep.FINANCIAL_OBLIGATION,
        analytics: {
            type: AnalyticType.AMOUNT,
            range: [min, 5000, 10000, 25000, 50000, max]
        }
    },
    {
        key: QuestionKey.CREDIT_CARD_BUTTON_GROUP,
        step: AccordionStep.FINANCIAL_OBLIGATION
    },
    {
        key: QuestionKey.CREDIT_CARD_AMOUNT,
        validator: {
            min: 1,
            max: 20000
        },
        step: AccordionStep.FINANCIAL_OBLIGATION,
        analytics: {
            type: AnalyticType.AMOUNT,
            range: [min, 2500, 5000, 25000, 25000, max]
        }
    },
    {
        key: QuestionKey.PRIVATE_LEASE_AMOUNT,
        validator: {
            min: 0,
            max: 3000
        },
        step: AccordionStep.FINANCIAL_OBLIGATION,
        analytics: {
            type: AnalyticType.AMOUNT,
            range: [min, 250, 500, 750, 1000, max]
        }
    },
    {
        key: QuestionKey.PRIVATE_LEASE_MONTHS,
        validator: {
            min: 0,
            max: 84
        },
        step: AccordionStep.FINANCIAL_OBLIGATION,
        analytics: {
            type: AnalyticType.AMOUNT,
            range: [min, 12, 24, 36, 48, 60, max]
        }
    },
    {
        key: QuestionKey.PRIVATE_LEASE_AMOUNT_CO_APPLICANT,
        validator: {
            min: 0,
            max: 3000
        },
        step: AccordionStep.FINANCIAL_OBLIGATION,
        analytics: {
            type: AnalyticType.AMOUNT,
            range: [min, 250, 500, 750, 1000, max]
        }
    },
    {
        key: QuestionKey.PRIVATE_LEASE_MONTHS_CO_APPLICANT,
        validator: {
            min: 0,
            max: 84
        },
        step: AccordionStep.FINANCIAL_OBLIGATION,
        analytics: {
            type: AnalyticType.AMOUNT,
            range: [min, 12, 24, 36, 48, 60, max]
        }
    },
    {
        key: QuestionKey.PERSONAL_LOAN_TOTAL_AMOUNT,
        validator: {
            min: 1,
            max: 250000
        },
        step: AccordionStep.FINANCIAL_OBLIGATION,
        analytics: {
            type: AnalyticType.AMOUNT,
            range: [min, 5000, 10000, 15000, 25000, 50000, max]
        }
    },
    {
        key: QuestionKey.PERSONAL_LOAN_MONTHLY_PAYMENT,
        validator: {
            min: 1,
            max: 7500
        },
        step: AccordionStep.FINANCIAL_OBLIGATION,
        analytics: {
            type: AnalyticType.AMOUNT,
            range: [min, 250, 500, 750, 1000, max]
        }
    },
    {
        key: QuestionKey.PARTNER_ALIMONY_AMOUNT,
        validator: {
            min: 1,
            max: 20000
        },
        step: AccordionStep.FINANCIAL_OBLIGATION,
        analytics: {
            type: AnalyticType.AMOUNT,
            range: [min, 250, 500, 750, 1000, max]
        }
    },
    {
        key: QuestionKey.PURCHASE_PRICE_AMOUNT,
        validator: {
            min: 1,
            max: 9999999
        },
        step: AccordionStep.PROPERTY,
        analytics: {
            type: AnalyticType.AMOUNT,
            range: [min, 200000, 250000, 300000, 350000, 400000, 450000, 500000, 600000, 750000, 1000000, max]
        }
    },
    {
        key: QuestionKey.PROPERTY_TYPE_BUTTON_GROUP,
        step: AccordionStep.PROPERTY
    },
    {
        key: QuestionKey.ENERGY_LABEL_RADIO,
        step: AccordionStep.PROPERTY
    },
    {
        key: QuestionKey.SUSTAINABILITY_LOAN_BUTTON_GROUP,
        step: AccordionStep.PROPERTY
    },
    {
        key: QuestionKey.SUSTAINABILITY_AMOUNT,
        validator: {
            min: 1,
            max: 9999999
        },
        step: AccordionStep.PROPERTY,
        analytics: {
            type: AnalyticType.AMOUNT,
            range: [min, 2500, 5000, 9000, 15000, 20000, 30000, 40000, 50000, 75000, 100000, max]
        }
    },
    {
        key: QuestionKey.RENOVATION,
        step: AccordionStep.PROPERTY
    },
    {
        key: QuestionKey.RENOVATION_AMOUNT,
        validator: {
            min: 1,
            max: 9999999
        },
        step: AccordionStep.PROPERTY,
        analytics: {
            type: AnalyticType.AMOUNT,
            range: [min, 2500, 5000, 10000, 15000, 20000, 30000, 40000, 50000, 75000, 100000, 150000, 250000, max]
        }
    },
    {
        key: QuestionKey.OWN_PROPERTY,
        step: AccordionStep.EXTRA_ASSET
    },
    {
        key: QuestionKey.SELLING_PRICE,
        validator: {
            min: 1,
            max: 9999999
        },
        step: AccordionStep.EXTRA_ASSET,
        analytics: {
            type: AnalyticType.AMOUNT,
            range: [min, 200000, 250000, 300000, 350000, 400000, 450000, 500000, 600000, 750000, 1000000, max]
        }
    },
    {
        key: QuestionKey.MORTGAGE_AMOUNT,
        validator: {
            min: 0,
            max: 9999999
        },
        step: AccordionStep.EXTRA_ASSET,
        analytics: {
            type: AnalyticType.AMOUNT,
            range: [min, 50000, 100000, 150000, 200000, 300000, 350000, 400000, 450000, 500000, 750000, 1000000, max]
        }
    },
    {
        key: QuestionKey.OWN_MONEY,
        step: AccordionStep.EXTRA_ASSET
    },
    {
        key: QuestionKey.OWN_MONEY_AMOUNT,
        validator: {
            min: 1,
            max: 9999999
        },
        step: AccordionStep.EXTRA_ASSET,
        analytics: {
            type: AnalyticType.AMOUNT,
            range: [min, 5000, 10000, 15000, 20000, 25000, 30000, 40000, 50000, 75000, 100000, 150000, 200000, max]
        }
    },
    {
        key: ButtonKey.MORTGAGE_INCOME,
        step: AccordionStep.MORTGAGE,
        analytics: {
            type: AnalyticType.AMOUNT,
            range: [min, 100000, 150000, 200000, 250000, 300000, 350000, 400000, 450000, 500000, 600000, 750000, 1000000, max]
        }
    },
    {
        key: ButtonKey.INCOME_CALCULATOR,
        step: AccordionStep.INCOME
    },
    {
        key: ButtonKey.MORTGAGE_INCOME_GROSS_AMOUNT,
        step: AccordionStep.MORTGAGE
    },
    {
        key: ButtonKey.MORTGAGE_INCOME_NET_AMOUNT,
        step: AccordionStep.MORTGAGE
    },
    {
        key: ButtonKey.EXPECTED_MORTGAGE,
        step: AccordionStep.MORTGAGE,
        analytics: {
            type: AnalyticType.AMOUNT,
            range: [min, 100000, 150000, 200000, 250000, 300000, 350000, 400000, 450000, 500000, 600000, 750000, 1000000, max]
        }
    },
    {
        key: ButtonKey.EXPECTED_MORTGAGE_GROSS_AMOUNT,
        step: AccordionStep.MORTGAGE
    },
    {
        key: ButtonKey.EXPECTED_MORTGAGE_NET_AMOUNT,
        step: AccordionStep.MORTGAGE
    },
    {
        key: ButtonKey.CLOSING_COST_TRANSFER_TAX,
        step: AccordionStep.MORTGAGE
    },
    {
        key: ButtonKey.CLOSING_COST_NHG,
        step: AccordionStep.MORTGAGE
    },
    {
        key: ButtonKey.CLOSING_COST_BANK_GUARANTEE,
        step: AccordionStep.MORTGAGE
    },
    {
        key: ButtonKey.PURCHASE_COST_PROPERTY,
        step: AccordionStep.MORTGAGE
    },
    {
        key: ButtonKey.REQUIRED_ASSET_DEFICIENCY,
        step: AccordionStep.MORTGAGE
    },
    {
        key: ButtonKey.REQUIRED_ASSET_OWN_INVESTMENT_RENOVATION,
        step: AccordionStep.MORTGAGE
    },
    {
        key: ButtonKey.REQUIRED_ASSET_OWN_INVESTMENT_SUSTAINABILITY,
        step: AccordionStep.MORTGAGE
    },
    {
        key: ButtonKey.CALCULATION_DETAILS_BUTTON,
        step: AccordionStep.MORTGAGE
    },
    {
        key: ButtonKey.APPOINTMENT_BUTTON,
        step: AccordionStep.MORTGAGE
    }
];

export function getDefaultEnergyLabel(propertyType?: PropertyType): EnergyLabel {
    return propertyType === PropertyType.NEW_BUILD ? EnergyLabel.APPP : EnergyLabel.A;
}
